import React, { useState, useEffect } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import SunCalc from "suncalc";
import translate from "../../i18n/translate";

// Custom Moon SVG Component that accurately displays illumination
const MoonPhaseVisual = ({ phase, size = 40 }) => {
  // phase: 0 = new moon, 0.5 = full moon, 1 = new moon again
  const illumination = phase * 100;
  
  // Determine if waxing (0-0.5) or waning (0.5-1)
  const isWaxing = phase < 0.5;
  
  // Calculate accurate terminator position using astronomical formula
  // phase 0: new moon (0% lit)
  // phase 0.25: first quarter (50% lit, right side)
  // phase 0.5: full moon (100% lit)
  // phase 0.75: last quarter (50% lit, left side)
  const k = phase * 2; // Normalize to 0-2
  
  // Create path for the illuminated portion
  const getMoonPath = () => {
    const r = size / 2;
    const cx = size / 2;
    const cy = size / 2;
    
    if (phase < 0.02) {
      // New Moon - tiny crescent or nothing
      return null;
    } else if (phase > 0.98) {
      // New Moon (end of cycle)
      return null;
    } else if (phase > 0.48 && phase < 0.52) {
      // Full Moon
      return `M ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy + 0.01}`;
    } else if (isWaxing) {
      // Waxing: 0 to 0.5
      // Calculate the width of illuminated crescent
      const offsetX = r * Math.cos(phase * Math.PI * 2);
      const sweep = phase < 0.25 ? 0 : 1;
      
      return `
        M ${cx} ${cy - r}
        A ${r} ${r} 0 0 1 ${cx} ${cy + r}
        A ${Math.abs(offsetX)} ${r} 0 0 ${sweep} ${cx} ${cy - r}
      `;
    } else {
      // Waning: 0.5 to 1
      const offsetX = r * Math.cos(phase * Math.PI * 2);
      const sweep = phase > 0.75 ? 0 : 1;
      
      return `
        M ${cx} ${cy - r}
        A ${r} ${r} 0 0 0 ${cx} ${cy + r}
        A ${Math.abs(offsetX)} ${r} 0 0 ${sweep} ${cx} ${cy - r}
      `;
    }
  };
  
  const moonPath = getMoonPath();
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        {/* Gradient for 3D effect */}
        <radialGradient id={`moonGradient-${size}`} cx="35%" cy="35%">
          <stop offset="0%" stopColor="#f0f0f0" />
          <stop offset="50%" stopColor="#e0e0e0" />
          <stop offset="100%" stopColor="#c0c0c0" />
        </radialGradient>
      </defs>
      
      {/* Dark side of moon (background circle) */}
      <circle cx={size/2} cy={size/2} r={size/2} fill="#1a1a1a" />
      
      {/* Illuminated portion */}
      {moonPath && (
        <path 
          d={moonPath} 
          fill={`url(#moonGradient-${size})`}
          stroke="none"
        />
      )}
      
      {/* Moon outline for definition */}
      <circle 
        cx={size/2} 
        cy={size/2} 
        r={size/2} 
        fill="none" 
        stroke="rgba(200,200,200,0.4)" 
        strokeWidth="0.5"
      />
    </svg>
  );
};

const AstroWidget = ({ compact = false }) => {
  const [astroData, setAstroData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [temperature, setTemperature] = useState(null);
  const user = useSelector((state) => state.auth.user);

  // Country coordinates
  const coordinates = {
    Palestine: { lat: 31.9522, lon: 35.2332, timezone: "Asia/Jerusalem" },
    Israel: { lat: 31.0461, lon: 34.8516, timezone: "Asia/Jerusalem" },
    Jordan: { lat: 31.9454, lon: 35.9284, timezone: "Asia/Amman" },
  };

  const location = coordinates[user?.country] || coordinates.Palestine;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    calculateAstro();
    fetchTemperature();

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    calculateAstro();
    fetchTemperature();
  }, [currentTime, user?.country]);

  const fetchTemperature = async () => {
    try {
      // Using Open-Meteo API (completely free, no API key required)
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${location.lat}&longitude=${location.lon}&current_weather=true&temperature_unit=celsius`
      );
      const data = await response.json();
      console.log('Temperature API response:', data);
      if (data.current_weather && typeof data.current_weather.temperature !== 'undefined') {
        const temp = Math.round(data.current_weather.temperature);
        console.log('Setting temperature:', temp);
        setTemperature(temp);
      } else {
        console.log('No temperature data in response');
        setTemperature(null);
      }
    } catch (error) {
      console.error('Failed to fetch temperature:', error);
      setTemperature(null);
    }
  };

  const calculateAstro = () => {
    const now = new Date();
    
    // Get sun times
    const sunTimes = SunCalc.getTimes(now, location.lat, location.lon);
    
    // Get sun position
    const sunPos = SunCalc.getPosition(now, location.lat, location.lon);
    
    // Get moon data
    const moonPos = SunCalc.getMoonPosition(now, location.lat, location.lon);
    const moonIllum = SunCalc.getMoonIllumination(now);
    const moonTimes = SunCalc.getMoonTimes(now, location.lat, location.lon);
    
    // Calculate day length
    const dayLength = (sunTimes.sunset - sunTimes.sunrise) / 1000 / 60; // minutes
    
    setAstroData({
      sun: {
        times: sunTimes,
        position: sunPos,
        altitude: (sunPos.altitude * 180 / Math.PI).toFixed(1),
        azimuth: (sunPos.azimuth * 180 / Math.PI + 180).toFixed(1),
        dayLength: Math.floor(dayLength / 60) + 'h ' + Math.floor(dayLength % 60) + 'm',
      },
      moon: {
        times: moonTimes,
        position: moonPos,
        illumination: moonIllum,
        phase: moonIllum.phase,
        illuminatedPercent: (moonIllum.fraction * 100).toFixed(0),
      }
    });
  };

  if (!astroData) return null;

  const getMoonPhaseEmoji = (phase) => {
    if (phase < 0.03) return "🌑"; // New Moon
    if (phase < 0.22) return "🌒"; // Waxing Crescent
    if (phase < 0.28) return "🌓"; // First Quarter
    if (phase < 0.47) return "🌔"; // Waxing Gibbous
    if (phase < 0.53) return "🌕"; // Full Moon
    if (phase < 0.72) return "🌖"; // Waning Gibbous
    if (phase < 0.78) return "🌗"; // Last Quarter
    if (phase < 0.97) return "🌘"; // Waning Crescent
    return "🌑"; // New Moon
  };

  const getMoonPhaseName = (phase) => {
    if (phase < 0.03) return "New Moon";
    if (phase < 0.22) return "Waxing Crescent";
    if (phase < 0.28) return "First Quarter";
    if (phase < 0.47) return "Waxing Gibbous";
    if (phase < 0.53) return "Full Moon";
    if (phase < 0.72) return "Waning Gibbous";
    if (phase < 0.78) return "Last Quarter";
    if (phase < 0.97) return "Waning Crescent";
    return "New Moon";
  };

  const formatTime = (date) => {
    if (!date || isNaN(date.getTime())) return "--:--";
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const isDaytime = currentTime >= astroData.sun.times.sunrise && currentTime <= astroData.sun.times.sunset;
  const isWaxing = astroData.moon.phase < 0.5;
  
  // Current sun and moon visibility
  const sunVisible = astroData.sun.position.altitude > 0;
  const moonVisible = astroData.moon.position.altitude > 0;

  // Compact version for navbar
  if (compact) {
    return (
      <Tooltip 
        title={
          <Box sx={{ p: 1 }}>
            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
              <strong>🌡️ {translate("Temperature")}:</strong> {temperature !== null ? `${temperature}°C` : 'Loading...'}
            </Typography>
            <Typography variant="caption" display="block">
              <strong>{translate("Sun")}:</strong> ↑{formatTime(astroData.sun.times.sunrise)} ↓{formatTime(astroData.sun.times.sunset)}
            </Typography>
            <Typography variant="caption" display="block">
              <strong>{translate("Moon")}:</strong> {translate(getMoonPhaseName(astroData.moon.phase))} ({astroData.moon.illuminatedPercent}%)
            </Typography>
          </Box>
        }
        arrow
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.4,
            px: 2.1,
            py: 1.1,
            borderRadius: "13px",
            background: isDaytime 
              ? "linear-gradient(135deg, #87CEEB 0%, #E0F6FF 100%)"
              : "linear-gradient(135deg, #0F2027 0%, #2C5364 100%)",
            cursor: "pointer",
            transition: "all 0.3s ease",
            minWidth: "111px",
            "&:hover": {
              transform: "scale(1.08)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }
          }}
        >
          <Box sx={{ fontSize: "34px", filter: sunVisible ? "drop-shadow(0 0 8px rgba(255,200,0,0.6))" : "opacity(0.3)" }}>
            ☀️
          </Box>
          <Typography 
            sx={{ 
              fontSize: "20px", 
              fontWeight: 700, 
              color: isDaytime ? "#333" : "#fff",
              textShadow: isDaytime ? "0 1px 2px rgba(0,0,0,0.2)" : "0 1px 2px rgba(255,255,255,0.3)",
            }}
          >
            {temperature !== null ? `${temperature}°` : '--°'}
          </Typography>
          <Box sx={{ filter: moonVisible ? "drop-shadow(0 0 8px rgba(255,255,255,0.4))" : "opacity(0.3)" }}>
            <MoonPhaseVisual phase={astroData.moon.phase} size={34} />
          </Box>
        </Box>
      </Tooltip>
    );
  }

  // Full version for expanded display
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "836px",
        margin: "17px auto",
        padding: "34px",
        background: isDaytime 
          ? "linear-gradient(to bottom, #87CEEB 0%, #E0F6FF 100%)"
          : "linear-gradient(to bottom, #0F2027 0%, #203A43 50%, #2C5364 100%)",
        borderRadius: "22px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
        transition: "all 0.5s ease",
        border: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      {/* Header */}
      <Typography 
        variant="h6" 
        sx={{ 
          textAlign: "center", 
          color: isDaytime ? "#333" : "#fff",
          fontWeight: 700,
          mb: 4.3,
          fontSize: "25px",
        }}
      >
        {translate(user?.country || "Palestine")} • {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Typography>

      {/* Sun Section */}
      <Box sx={{ mb: 4.3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.8 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.8 }}>
            <Box sx={{ fontSize: "56px", filter: sunVisible ? "drop-shadow(0 0 12px rgba(255,200,0,0.8))" : "opacity(0.3)" }}>
              ☀️
            </Box>
            <Typography variant="h5" sx={{ color: isDaytime ? "#333" : "#fff", fontWeight: 700, fontSize: "26px" }}>
              {translate("Sun")}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: isDaytime ? "#666" : "#ccc", fontWeight: 600, fontSize: "17px" }}>
            {translate("Altitude")}: {astroData.sun.altitude}°
          </Typography>
        </Box>
        
        <Box sx={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(3, 1fr)", 
          gap: 2.8,
          fontSize: "19px",
          color: isDaytime ? "#555" : "#ddd",
        }}>
          <Tooltip title={translate("Sunrise")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>🌅</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Sunrise")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "20px" }}>{formatTime(astroData.sun.times.sunrise)}</Box>
            </Box>
          </Tooltip>
          <Tooltip title={translate("Solar Noon (Peak)")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>☀️</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Peak")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "20px" }}>{formatTime(astroData.sun.times.solarNoon)}</Box>
            </Box>
          </Tooltip>
          <Tooltip title={translate("Sunset")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>🌇</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Sunset")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "20px" }}>{formatTime(astroData.sun.times.sunset)}</Box>
            </Box>
          </Tooltip>
          <Tooltip title={translate("Dawn")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>🌄</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Dawn")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "20px" }}>{formatTime(astroData.sun.times.dawn)}</Box>
            </Box>
          </Tooltip>
          <Tooltip title={translate("Day Length")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>⏱️</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Length")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "20px" }}>{astroData.sun.dayLength}</Box>
            </Box>
          </Tooltip>
          <Tooltip title={translate("Dusk")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>🌆</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Dusk")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "20px" }}>{formatTime(astroData.sun.times.dusk)}</Box>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ height: "2px", background: isDaytime ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)", my: 4.3 }} />

      {/* Moon Section */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.8 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.8 }}>
            <Box sx={{ filter: moonVisible ? "drop-shadow(0 0 12px rgba(255,255,255,0.6))" : "opacity(0.3)" }}>
              <MoonPhaseVisual phase={astroData.moon.phase} size={70} />
            </Box>
            <Typography variant="h5" sx={{ color: isDaytime ? "#333" : "#fff", fontWeight: 700, fontSize: "26px" }}>
              {translate("Moon")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
            <Typography variant="body1" sx={{ color: isDaytime ? "#666" : "#ccc", fontWeight: 600, fontSize: "17px" }}>
              {isWaxing ? "📈" : "📉"} {astroData.moon.illuminatedPercent}%
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(3, 1fr)", 
          gap: 2.8,
          fontSize: "19px",
          color: isDaytime ? "#555" : "#ddd",
        }}>
          <Tooltip title={translate("Moon Phase")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 0.8 }}>
                <MoonPhaseVisual phase={astroData.moon.phase} size={43} />
              </Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Phase")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "17px" }}>{translate(getMoonPhaseName(astroData.moon.phase))}</Box>
            </Box>
          </Tooltip>
          <Tooltip title={translate("Moonrise")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>🌙</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Rise")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "20px" }}>{formatTime(astroData.moon.times.rise)}</Box>
            </Box>
          </Tooltip>
          <Tooltip title={translate("Moonset")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>🌙</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Set")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "20px" }}>{formatTime(astroData.moon.times.set)}</Box>
            </Box>
          </Tooltip>
          <Tooltip title={translate("New Moon (0%)")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>🌑</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("New Moon")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "17px" }}>
                {astroData.moon.phase < 0.03 ? translate("Today!") : translate("Soon")}
              </Box>
            </Box>
          </Tooltip>
          <Tooltip title={isWaxing ? translate("Waxing (Growing)") : translate("Waning (Shrinking)")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>{isWaxing ? "📈" : "📉"}</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{isWaxing ? translate("Waxing") : translate("Waning")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "17px" }}>
                {isWaxing ? translate("Growing") : translate("Shrinking")}
              </Box>
            </Box>
          </Tooltip>
          <Tooltip title={translate("Full Moon (100%)")} arrow>
            <Box sx={{ textAlign: "center", p: 1.4, borderRadius: "11px", background: "rgba(255,255,255,0.1)" }}>
              <Box sx={{ fontSize: "28px" }}>🌕</Box>
              <Box sx={{ fontSize: "15px", mb: 0.8 }}>{translate("Full Moon")}</Box>
              <Box sx={{ fontWeight: 700, fontSize: "17px" }}>
                {astroData.moon.phase >= 0.47 && astroData.moon.phase <= 0.53 ? translate("Today!") : translate("Soon")}
              </Box>
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default AstroWidget;
